/**
 * A real Excel file (.xlsx) without a library: the few XML parts a workbook needs, in a zip that stores them
 * uncompressed. Enough for what Backoffice exports — tables of text, whole numbers, kroner and dates, a bold header row
 * kept in view — and nothing more. Excel, Numbers and LibreOffice open it.
 *
 * Why not a library: Backoffice had none, the SheetJS build on npm is old and has known security holes (its maintained
 * builds are not on npm), and a few hundred lines of output do not justify a dependency.
 *
 * A text cell is a string, never a formula, so a value starting with = is shown, not run — unlike CSV, where such a
 * value must be guarded.
 */

const encoder = new TextEncoder();

// ── Cells ─────────────────────────────────────────────────────────────────────────────────────────────────

/** The styles in styles.xml below, by their index in cellXfs. */
const STYLE = { plain: 0, bold: 1, money: 2, moneyBold: 3, date: 4, dateTime: 5, wholeBold: 6 };

const XlsxWriter = {
    text(value, bold) {
        return { kind: "text", value: value === null || value === undefined ? "" : String(value), style: bold ? STYLE.bold : STYLE.plain };
    },

    header(value) {
        return this.text(value, true);
    },

    whole(value, bold) {
        return { kind: "number", value: String(Math.trunc(Number(value) || 0)), style: bold ? STYLE.wholeBold : STYLE.plain };
    },

    /** Kroner from øre, exactly: built from the whole øre, never through a float. */
    money(ore, bold) {
        const n = Math.trunc(Number(ore) || 0);
        const abs = Math.abs(n);
        const value = (n < 0 ? "-" : "") + Math.floor(abs / 100) + "." + String(abs % 100).padStart(2, "0");
        return { kind: "number", value, style: bold ? STYLE.moneyBold : STYLE.money };
    },

    /** A calendar day «2026-10-14», shown as 14.10.2026 and sortable as a date. */
    date(day) {
        if (!day) { return this.text(""); }
        const [y, m, d] = day.split("-").map(Number);
        return { kind: "number", value: String(serial(y, m, d, 0, 0, 0)), style: STYLE.date };
    },

    /** A moment, shown in Norwegian time as 14.10.2026 06:25. */
    dateTime(iso) {
        if (!iso) { return this.text(""); }
        const parts = new Intl.DateTimeFormat("en-GB", {
            timeZone: "Europe/Oslo", year: "numeric", month: "2-digit", day: "2-digit",
            hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23"
        }).formatToParts(new Date(iso));
        const get = (type) => Number((parts.find((p) => p.type === type) || {}).value || 0);
        return { kind: "number",
            value: String(serial(get("year"), get("month"), get("day"), get("hour"), get("minute"), get("second"))),
            style: STYLE.dateTime };
    },

    // ── The file ──────────────────────────────────────────────────────────────────────────────────────────

    /**
     * @param sheets [{ name, widths: [characters per column], rows: [[cell]] }], the first row a header kept in view
     * @return the .xlsx file's bytes
     */
    workbook(sheets) {
        const files = [
            { name: "[Content_Types].xml", xml: contentTypes(sheets.length) },
            { name: "_rels/.rels", xml: RELS },
            { name: "xl/workbook.xml", xml: workbookXml(sheets) },
            { name: "xl/_rels/workbook.xml.rels", xml: workbookRels(sheets.length) },
            { name: "xl/styles.xml", xml: STYLES }
        ];
        sheets.forEach((sheet, i) => files.push({ name: "xl/worksheets/sheet" + (i + 1) + ".xml", xml: sheetXml(sheet) }));
        return zip(files.map((f) => ({ name: f.name, data: encoder.encode(f.xml) })));
    }
};

/** Excel's day number: days since 30.12.1899, the time of day as the fraction. */
function serial(y, m, d, hour, minute, second) {
    const days = (Date.UTC(y, m - 1, d) - Date.UTC(1899, 11, 30)) / 86400000;
    const fraction = (hour * 3600 + minute * 60 + second) / 86400;
    return fraction ? Number((days + fraction).toFixed(10)) : days;
}

function escape(s) {
    let clean = "";
    for (const ch of String(s)) {
        const code = ch.charCodeAt(0);
        // Control characters other than tab and line breaks are not allowed in XML 1.0 at all.
        if (code >= 32 || code === 9 || code === 10 || code === 13) {
            clean += ch;
        }
    }
    return clean.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

/** A, B … Z, AA … */
function column(index) {
    let name = "";
    for (let n = index + 1; n > 0; n = Math.floor((n - 1) / 26)) {
        name = String.fromCharCode(65 + ((n - 1) % 26)) + name;
    }
    return name;
}

const XML_HEAD = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n';
const MAIN = "http://schemas.openxmlformats.org/spreadsheetml/2006/main";
const REL = "http://schemas.openxmlformats.org/officeDocument/2006/relationships";

function contentTypes(count) {
    let sheets = "";
    for (let i = 1; i <= count; i++) {
        sheets += '<Override PartName="/xl/worksheets/sheet' + i + '.xml" '
            + 'ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>';
    }
    return XML_HEAD + '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">'
        + '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>'
        + '<Default Extension="xml" ContentType="application/xml"/>'
        + '<Override PartName="/xl/workbook.xml" '
        + 'ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>'
        + '<Override PartName="/xl/styles.xml" '
        + 'ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>'
        + sheets + '</Types>';
}

const RELS = XML_HEAD + '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">'
    + '<Relationship Id="rId1" Type="' + REL + '/officeDocument" Target="xl/workbook.xml"/></Relationships>';

function workbookXml(sheets) {
    const list = sheets.map((sheet, i) =>
        '<sheet name="' + escape(sheet.name) + '" sheetId="' + (i + 1) + '" r:id="rId' + (i + 1) + '"/>').join("");
    return XML_HEAD + '<workbook xmlns="' + MAIN + '" xmlns:r="' + REL + '"><sheets>' + list + '</sheets></workbook>';
}

function workbookRels(count) {
    let rels = "";
    for (let i = 1; i <= count; i++) {
        rels += '<Relationship Id="rId' + i + '" Type="' + REL + '/worksheet" Target="worksheets/sheet' + i + '.xml"/>';
    }
    rels += '<Relationship Id="rId' + (count + 1) + '" Type="' + REL + '/styles" Target="styles.xml"/>';
    return XML_HEAD + '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' + rels
        + '</Relationships>';
}

// Built-in number formats: 1 is «0», 4 is «#,##0.00», which Excel shows with the reader's own separators.
const STYLES = XML_HEAD + '<styleSheet xmlns="' + MAIN + '">'
    + '<numFmts count="2"><numFmt numFmtId="164" formatCode="dd.mm.yyyy"/>'
    + '<numFmt numFmtId="165" formatCode="dd.mm.yyyy hh:mm"/></numFmts>'
    + '<fonts count="2"><font><sz val="11"/><name val="Calibri"/></font>'
    + '<font><b/><sz val="11"/><name val="Calibri"/></font></fonts>'
    + '<fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill></fills>'
    + '<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>'
    + '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>'
    + '<cellXfs count="7">'
    + '<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>'
    + '<xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/>'
    + '<xf numFmtId="4" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>'
    + '<xf numFmtId="4" fontId="1" fillId="0" borderId="0" xfId="0" applyNumberFormat="1" applyFont="1"/>'
    + '<xf numFmtId="164" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>'
    + '<xf numFmtId="165" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>'
    + '<xf numFmtId="1" fontId="1" fillId="0" borderId="0" xfId="0" applyNumberFormat="1" applyFont="1"/>'
    + '</cellXfs>'
    + '<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>'
    + '</styleSheet>';

function sheetXml(sheet) {
    const cols = (sheet.widths || []).map((width, i) =>
        '<col min="' + (i + 1) + '" max="' + (i + 1) + '" width="' + width + '" customWidth="1"/>').join("");
    const rows = sheet.rows.map((row, r) => {
        const cells = row.map((cell, c) => {
            if (!cell) { return ""; }
            const ref = column(c) + (r + 1);
            const style = cell.style ? ' s="' + cell.style + '"' : "";
            if (cell.kind === "number") {
                return '<c r="' + ref + '"' + style + '><v>' + cell.value + '</v></c>';
            }
            return '<c r="' + ref + '"' + style + ' t="inlineStr"><is><t xml:space="preserve">' + escape(cell.value)
                + '</t></is></c>';
        }).join("");
        return '<row r="' + (r + 1) + '">' + cells + '</row>';
    }).join("");
    return XML_HEAD + '<worksheet xmlns="' + MAIN + '">'
        + '<sheetViews><sheetView workbookViewId="0">'
        + '<pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>'
        + (cols ? '<cols>' + cols + '</cols>' : "")
        + '<sheetData>' + rows + '</sheetData></worksheet>';
}

// ── Zip, stored ───────────────────────────────────────────────────────────────────────────────────────────

const CRC_TABLE = (() => {
    const table = new Uint32Array(256);
    for (let n = 0; n < 256; n++) {
        let c = n;
        for (let k = 0; k < 8; k++) {
            c = c & 1 ? 0xEDB88320 ^ (c >>> 1) : c >>> 1;
        }
        table[n] = c >>> 0;
    }
    return table;
})();

function crc32(bytes) {
    let crc = 0xFFFFFFFF;
    for (let i = 0; i < bytes.length; i++) {
        crc = CRC_TABLE[(crc ^ bytes[i]) & 0xFF] ^ (crc >>> 8);
    }
    return (crc ^ 0xFFFFFFFF) >>> 0;
}

/** A zip of [{ name, data }], every entry stored as it is (method 0), dated 1.1.1980. */
function zip(files) {
    const parts = [];
    const central = [];
    let offset = 0;
    for (const file of files) {
        const name = encoder.encode(file.name);
        const crc = crc32(file.data);
        const size = file.data.length;

        const local = new DataView(new ArrayBuffer(30));
        local.setUint32(0, 0x04034b50, true);
        local.setUint16(4, 20, true);         // version needed to extract
        local.setUint16(6, 0x0800, true);     // names are UTF-8
        local.setUint16(8, 0, true);          // stored
        local.setUint16(10, 0, true);         // time
        local.setUint16(12, 0x21, true);      // date: 1.1.1980
        local.setUint32(14, crc, true);
        local.setUint32(18, size, true);
        local.setUint32(22, size, true);
        local.setUint16(26, name.length, true);
        local.setUint16(28, 0, true);
        parts.push(new Uint8Array(local.buffer), name, file.data);

        const entry = new DataView(new ArrayBuffer(46));
        entry.setUint32(0, 0x02014b50, true);
        entry.setUint16(4, 20, true);         // made by
        entry.setUint16(6, 20, true);         // needed
        entry.setUint16(8, 0x0800, true);
        entry.setUint16(10, 0, true);
        entry.setUint16(12, 0, true);
        entry.setUint16(14, 0x21, true);
        entry.setUint32(16, crc, true);
        entry.setUint32(20, size, true);
        entry.setUint32(24, size, true);
        entry.setUint16(28, name.length, true);
        entry.setUint16(30, 0, true);         // extra field
        entry.setUint16(32, 0, true);         // comment
        entry.setUint16(34, 0, true);         // disk
        entry.setUint16(36, 0, true);         // internal attributes
        entry.setUint32(38, 0, true);         // external attributes
        entry.setUint32(42, offset, true);    // where the local header is
        central.push(new Uint8Array(entry.buffer), name);

        offset += 30 + name.length + size;
    }
    const centralSize = central.reduce((n, part) => n + part.length, 0);
    const end = new DataView(new ArrayBuffer(22));
    end.setUint32(0, 0x06054b50, true);
    end.setUint16(4, 0, true);
    end.setUint16(6, 0, true);
    end.setUint16(8, files.length, true);
    end.setUint16(10, files.length, true);
    end.setUint32(12, centralSize, true);
    end.setUint32(16, offset, true);
    end.setUint16(20, 0, true);

    const all = [...parts, ...central, new Uint8Array(end.buffer)];
    const out = new Uint8Array(all.reduce((n, part) => n + part.length, 0));
    let at = 0;
    for (const part of all) {
        out.set(part, at);
        at += part.length;
    }
    return out;
}

export default XlsxWriter;
